import type { ChatOpeningHours, ChatOpeningHoursDay, ChatSettingsType } from '../../../server/chat-settings';

// Index matches Date.getUTCDay(): Sunday is 0.
const DAYS_BY_INDEX: ChatOpeningHoursDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Display order, Monday first.
const WEEK: ChatOpeningHoursDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

type ZonedTime = {
    day: ChatOpeningHoursDay;
    minutes: number;
};

const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + (minutes || 0);
};

const getUtcTime = (date: Date): ZonedTime => ({
    day: DAYS_BY_INDEX[date.getUTCDay()],
    minutes: date.getUTCHours() * 60 + date.getUTCMinutes(),
});

// WordPress returns either an IANA name ("Europe/Stockholm") or a fixed offset ("+01:00").
export const getZonedTime = (now: Date, timezone?: string): ZonedTime => {
    if (timezone && /^[+-]\d{1,2}(:\d{2})?$/.test(timezone)) {
        const sign = timezone.startsWith('-') ? -1 : 1;
        const offsetInMinutes = sign * toMinutes(timezone.slice(1));
        return getUtcTime(new Date(now.getTime() + offsetInMinutes * 60 * 1000));
    }

    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone || 'UTC',
            weekday: 'long',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(now);
        const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
        const day = part('weekday').toLowerCase() as ChatOpeningHoursDay;

        if (DAYS_BY_INDEX.includes(day)) {
            return { day, minutes: (Number(part('hour')) % 24) * 60 + Number(part('minute')) };
        }
    } catch {
        // Invalid timezone, fall back to UTC
    }

    return getUtcTime(now);
};

const getDayHours = (openingHours: ChatOpeningHours | undefined, day: ChatOpeningHoursDay) => {
    const hours = openingHours?.[day];
    return hours?.start && hours?.end ? hours : null;
};

export const isChatOpen = (settings: ChatSettingsType, now = new Date()) => {
    if (!settings.enabled) {
        return false;
    }

    if (!settings.limitOpeningHours) {
        return true;
    }

    const { day, minutes } = getZonedTime(now, settings.timezone);
    const hours = getDayHours(settings.openingHours, day);

    if (!hours) {
        return false;
    }

    return minutes >= toMinutes(hours.start) && minutes < toMinutes(hours.end);
};

const getShortDayNames = (locale?: string) => {
    const format = (formatLocale: string) => {
        const formatter = new Intl.DateTimeFormat(formatLocale, { weekday: 'short', timeZone: 'UTC' });
        // 2024-01-01 is a Monday
        return WEEK.map((_, index) => formatter.format(new Date(Date.UTC(2024, 0, 1 + index))));
    };

    try {
        return format(locale || 'en');
    } catch {
        return format('en');
    }
};

// Groups consecutive days with identical hours, e.g. "Mon–Fri 08:00–17:00, Sat 10:00–14:00".
// Closed days are left out.
export const formatOpeningHours = (openingHours: ChatOpeningHours | undefined, locale?: string) => {
    const dayNames = getShortDayNames(locale);
    const groups: { from: number; to: number; hours: string }[] = [];

    WEEK.forEach((day, index) => {
        const dayHours = getDayHours(openingHours, day);
        if (!dayHours) {
            return;
        }

        const hours = `${dayHours.start}–${dayHours.end}`;
        const previous = groups[groups.length - 1];

        if (previous && previous.to === index - 1 && previous.hours === hours) {
            previous.to = index;
        } else {
            groups.push({ from: index, to: index, hours });
        }
    });

    return groups
        .map(({ from, to, hours }) => {
            const days = from === to ? dayNames[from] : `${dayNames[from]}–${dayNames[to]}`;
            return `${days} ${hours}`;
        })
        .join(', ');
};
